import { useEffect, useRef } from "@bestowinc/tinkertoy-shell-deps/react";
import { page } from "@bestowinc/logger/client";
import { Helmet } from "react-helmet";
import { useLocation, useNavigation } from "react-router";

import type { ViewModuleRef } from "@bestowinc/tinkertoy-core";

import {
  FAILURE_EVENT_TAGS,
  isViewLoadError,
  markFailureReported,
  reportFailureOnce,
  resolveFailureError,
} from "../error-telemetry";
import {
  LoadingModuleHost,
  loadingModuleKey,
} from "../loading/loading-module-host";
import { useLoadingSlot } from "../loading/loading-slot-context";
import { LOADING_DELAY_MS, useDelayedFlag } from "../loading/use-delayed-flag";
import { useHeldLoadingDisplay } from "../loading/use-held-loading-display";
import { useModuleLoader } from "../module-loader/module-loader-context";
import { ViewRenderer } from "../view-renderer";

import { ShellLoaderError } from "./shell-loader-error";
import { useShellLoaderData } from "./use-shell-loader-data";
import { useShellViewLoadState } from "./use-shell-view-load-state";

function getDefaultAnalyticsPageName(viewId: string): string {
  const segments = viewId.split("/").filter(Boolean);
  const finalSegment = segments.at(-1);
  return finalSegment ? `/${finalSegment}` : "/";
}

const viewLoadShellErrors = new WeakMap<object, ShellLoaderError>();

/**
 * One {@link ShellLoaderError} per failed view import, so React's re-render of
 * a throwing component rethrows the same error, with the same trace ID, that
 * was reported.
 */
function getViewLoadShellError(
  error: unknown,
  viewId: string,
  viewVersion: string,
): ShellLoaderError {
  const isCacheable = typeof error === "object" && error !== null;
  const cached = isCacheable ? viewLoadShellErrors.get(error) : undefined;
  if (cached !== undefined) {
    return cached;
  }

  const shellError = new ShellLoaderError({
    message: `Failed to load view "${viewId}" (${viewVersion}).`,
    status: 500,
    code: "views_load_failed",
    errorContext: { errorType: "views_load_failed" },
  });

  if (isViewLoadError(error)) {
    const failureError = resolveFailureError(error, shellError.message);
    reportFailureOnce(failureError, {
      tag: FAILURE_EVENT_TAGS.viewBundleLoad,
      errorCode: error.code,
      message: failureError.message,
      error: failureError,
      viewId: error.viewId,
      viewVersion: error.version,
      traceId: shellError.traceId,
    });
    markFailureReported(shellError);
  }

  if (isCacheable) {
    viewLoadShellErrors.set(error, shellError);
  }
  return shellError;
}

/**
 * The single component rendered by the catch-all route.
 *
 * Reads the `/load` response via {@link useShellLoaderData}, imports the matched
 * view's bundle from the CDN (the wire response omits the live `View` — its
 * functions can't be serialized), and hands the definition plus every
 * server-resolved field (layout, module versions, resolved interactions, path
 * params) to {@link ViewRenderer}.
 *
 * The base `<div data-testid="tinkertoy-shell">` wrapper renders in every state
 * (including while the view bundle is loading) so the document title is present
 * immediately. A view-bundle load failure is rethrown as a
 * {@link ShellLoaderError} so the route's `errorElement`
 * ({@link import("./ShellErrorBoundary").ShellErrorBoundary}) handles it the
 * same way it handles a server-side loader failure.
 */
export function TinkertoyShell() {
  const data = useShellLoaderData();
  const location = useLocation();
  const moduleLoader = useModuleLoader();

  const { loadedView } = data;
  const { viewId, viewVersion } = loadedView;
  const navigationKey = location.key;

  const state = useShellViewLoadState();

  // A client navigation to another path that's still pending after
  // LOADING_DELAY_MS swaps the view's content for the loading module inside
  // the mounted layout. Same-path navigations and revalidations (which leave
  // `navigation.state` idle) keep the view as is.
  const navigation = useNavigation();
  const navigating =
    navigation.state === "loading" &&
    navigation.location.pathname !== location.pathname;
  const showLoading = useDelayedFlag(navigating, LOADING_DELAY_MS);
  const loadingDisplay = useHeldLoadingDisplay(useLoadingSlot(), navigating);
  const loadingContent =
    showLoading && loadingDisplay !== undefined ? (
      <LoadingModuleHost
        key={loadingModuleKey(loadingDisplay.loadingModule)}
        loadingModule={loadingDisplay.loadingModule}
        placement="layout"
      />
    ) : undefined;

  // Pageview analytics is keyed by navigation instead: one emit per completed
  // navigation, tracked by the last key we emitted for. The ref (rather than
  // effect deps alone) keeps ordinary rerenders and StrictMode's effect replay
  // from double-counting a navigation.
  const lastEmittedNavigationKey = useRef<string | undefined>(undefined);
  const readyView =
    state.status === "ready" &&
    state.viewId === viewId &&
    state.viewVersion === viewVersion
      ? state.view
      : undefined;
  const readyPageName =
    readyView !== undefined
      ? (readyView.meta?.analyticsPageName ??
        getDefaultAnalyticsPageName(viewId))
      : undefined;
  useEffect(() => {
    if (readyPageName === undefined) {
      return;
    }
    if (lastEmittedNavigationKey.current === navigationKey) {
      return;
    }
    lastEmittedNavigationKey.current = navigationKey;
    page(readyPageName);
  }, [navigationKey, readyPageName]);

  // Surface a failed view import through the route's error boundary, mirroring
  // how a server-side loader failure reaches it. Thrown in render so React
  // Router's errorElement (a React error boundary for this route) catches it.
  if (state.status === "error") {
    throw getViewLoadShellError(state.error, viewId, viewVersion);
  }

  // The layout arrives as a WireModuleRef; the server sends its static props
  // separately. When the view defines its own `layoutOverride`, ViewRenderer
  // re-evaluates that instead and ignores these.
  const layoutModuleRef: ViewModuleRef = {
    ...data.layoutModuleRef,
    props: data.layoutProps ?? {},
  };

  return (
    <div data-testid="tinkertoy-shell">
      {/* Flow-level base document title. ViewRenderer overrides it with the
          composed "<view> | <base>" title when the matched view has its own. */}
      <Helmet>
        <title>{data.flowMeta.title}</title>
      </Helmet>
      {readyView !== undefined && (
        <ViewRenderer
          key={`${viewId}@${viewVersion}`}
          view={readyView}
          viewVersion={viewVersion}
          viewData={data.viewData}
          moduleDataMap={data.moduleDataMap}
          layoutModuleRef={layoutModuleRef}
          resolvedModuleVersions={data.resolvedModuleVersions}
          resolvedInteractions={data.resolvedInteractions}
          currentPathParams={data.pathParams}
          loader={moduleLoader}
          loadingContent={loadingContent}
        />
      )}
    </div>
  );
}
